import { useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import ModalFlow from 'components/ModalFlow'
import Button from 'components/Button'
import Type from 'components/Type'
import Paper from 'components/Paper'
import Label from 'components/Label'
import TextArea from 'components/TextArea'
import Select from 'components/Select'
import IconButton from 'components/IconButton'
import Checkbox from 'components/Checkbox'
import TextButton from 'components/TextButton'
import Icon from 'components/Icon'
import CloudinaryUpload from 'components/CloudinaryUpload'
import CircleFrame from 'components/CommonUI/CircleFrame'
import DashedBoxButton from 'components/CommonUI/DashedBoxButton'
import AllSet from 'components/ModalViews/AllSet'
import { SEND_CUSTOMER_SERVICE_REQUEST } from 'api/constants'
import { useTypeLoad } from 'utils/hooks'
import { cloneDeep, findIndex } from 'lodash'
import { setCSImages, addCSImages } from 'slices/customerSupportSlice'

const formatDataToHTML = chatHistory => {
    let html = `
    <table style="width: 100%; border-collapse: collapse;">
      <tr style="background-color: #f2f2f2;">
        <th style="border: 1px solid #dddddd; text-align: left; padding: 8px;">Role</th>
        <th style="border: 1px solid #dddddd; text-align: left; padding: 8px;">Content</th>
      </tr>`

    chatHistory.forEach((item) => {
        html += `
        <tr>
          <td style="border: 1px solid #dddddd; text-align: left; padding: 8px;">${item.role}</td>
          <td style="border: 1px solid #dddddd; text-align: left; padding: 8px;">${item.content}</td>
        </tr>`
    })

    html += '</table>'
    return html
}


const RequestCustomerService = ({
  is_urgent,
}) => {
  const dispatch = useDispatch()
  const csImages = useSelector(({ customerSupport }) => customerSupport.csImages) || []
  const chatHistory = useSelector(({ customerSupport }) => customerSupport.chatHistory)
  const [activeIndex, setActiveIndex] = useState(0)
  const [emailType, setEmailType] = useState('')
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [isUrgent, setIsUrgent] = useState(!!is_urgent)
  const isDisabled = !emailType.length

  const onSendRequest = () => {
    setActiveIndex(-1)
    dispatch({
      type: SEND_CUSTOMER_SERVICE_REQUEST,
      data: {
        is_urgent: isUrgent,
        history: formatDataToHTML([...chatHistory, { role: 'user', content: additionalInfo }]),
        type: emailType,
        // images: csImages,
        attachment_data: {
          images: csImages,
        },
      },
    })
  }

  const handleOnUploadSuccess = imageArray => {
    if (imageArray?.length) {
      dispatch(addCSImages(imageArray.map(o => o.info)))
    }
  }

  const handleRemoveImage = image => {
    const temp = cloneDeep(csImages)
    temp.splice(findIndex(csImages, { secure_url: image.secure_url }), 1)
    dispatch(setCSImages(temp))
  }

  useTypeLoad(SEND_CUSTOMER_SERVICE_REQUEST, data => {
    if (!data.error) {
      setActiveIndex(1)
      dispatch(setCSImages([]))
    }
  })

  return (
    <ModalFlow  
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX="md"
    >
      {/* ADDITIONAL INFO */}
      <Stack
        display="grid"
        p={3}
      >
        <CircleFrame
          justifySelf="center"
          mb={2}
        >
          <Icon
            icon="CelTravelDesigner"
          />
        </CircleFrame>
        <Type
          variant="sectionLabel"
          textAlign="center"
          mb={1.5}
        >
          We’re here to help!
        </Type>
        <Type
          variant="p"
          textAlign="center"
          mb={1}
        >
          Please fill out this form and we’ll get back to you as soon as possible.
        </Type>
        <Type
          variant="dataSmall"
          color="neutralCool_700"
          textAlign="center"
          mb={3}
        >
          Your previous chat history will be shared with our team to ensure a comprehensive understanding of your needs.
        </Type>
        <Stack
          gap={2}
          mb={2}
        >
          <Select
            label="Support Type"
            value={emailType}
            onChange={e => setEmailType(e.target.value)}
          >
            <MenuItem
              value="support"
            >
              General Support
            </MenuItem>
            <MenuItem
              value="booking"
            >
              Booking
            </MenuItem>
          </Select>
          <Stack
            gap={1}
          >
            <Label>
              Additional Information
            </Label>
            <TextArea
              fluid
              noResize
              charCount={300}
              value={additionalInfo}
              onChange={setAdditionalInfo}
            />
          </Stack>
          <Stack
            gap={1}
          >
            <Label
              optional
            >
              Add Photos
            </Label>
            <CloudinaryUpload
              id="customer-service-upload"
              preset="CUSTOMER_SERVICE"
              reopenID="customer-service-upload"
              options="OPTIONS_CUSTOMER_SERVICE"
              dropzoneProps={{ allowBulk: true }}
              onBulkUploadSuccess={data => handleOnUploadSuccess(data, csImages)}
            >
              <DashedBoxButton>
                <Stack
                  sx={theme => theme.internalMixins.columns({ gap: 1 })}
                  py={1.5}
                  px={2}
                >
                  <Icon 
                    icon="UploadPhotos"
                    width="40"
                    height="40"
                  />
                  <div>
                    {/* <Type variant="bodyBoldSpaced" color="neutralCool_700">
                      Add your photos <Type variant="dataMicro" color="neutralCool_700" inline>(optional)</Type>
                    </Type> */}
                    <Type color="neutralCool_700" variant="p">
                      Drop your files here or{' '}
                      <TextButton
                        variant="buttonLarge"
                        inline
                        color="tertiary"
                      >
                        browse for a file
                      </TextButton>
                    </Type>
                  </div>
                </Stack>
              </DashedBoxButton>
            </CloudinaryUpload>
          </Stack>
          {!!csImages?.length && (
            <Stack
              direction="row"
              gap={2}
              flexWrap="wrap"
            >
              {csImages?.map(o => (
                <Box
                  key={o.secure_url}
                  sx={{
                    position: 'relative',
                    width: 75,
                    height: 75,
                    background: `url(${o.secure_url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <IconButton
                    as={Box}
                    solid
                    position="absolute"
                    top="-10px"
                    right="-10px"
                    zIndex="primary"
                    size="sm"
                    depth={3}
                    variant="inverse"
                    variantSolidHover="inverse"
                    color="inverse"
                    onClick={() => handleRemoveImage(o)}
                  >
                    <Icon icon="Close" />
                  </IconButton>
                  {o.secure_url?.endsWith('.pdf') && (
                    <Icon
                      icon="CelPDF"
                      width="75"
                      height="75"
                    />
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack
          gap={3}
          mb={4}
        >
          <Checkbox
            label="This request is urgent and requires immediate resolution"
            labelColor="secondary"
            align="top"
            checked={isUrgent}
            onChange={() => setIsUrgent(!isUrgent)}
          />
          {isUrgent && (
            <Paper
              round
              variant="tertiaryLight"
            >
              <Stack
                py={1}
                px={1.5}
              >
                <Type
                  variant="bodySmallBold"
                >
                  What will cause any delays in response?
                </Type>
                <Type
                  variant="dataSmall"
                >
                  Our regular business hours are Monday — Friday, from 9am to 5pm MST. Our office is closed on major holidays, which may impact our response time. We appreciate your understanding.
                </Type>
              </Stack>
            </Paper>
          )}
        </Stack>
        <Button
          justifyContent="center"
          disabled={isDisabled}
          onClick={onSendRequest}
        >
          Submit Request
        </Button>
      </Stack>
      {/* ALL SET */}
      <AllSet 
        subtitle="Customer support will reach out to you at their next availability"
        duration={5000}
      />
    </ModalFlow>
  )
}

export default RequestCustomerService